import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { useNotificationStore } from '../lib/notification-store';
import type { Notification, NotificationSubscription, PaginatedResponse } from './types';

const NOTIFICATIONS_KEY = ['notifications'] as const;
const UNREAD_COUNT_KEY = ['notifications', 'unread-count'] as const;
const subscriptionKey = (tableId: string) => ['tables', tableId, 'subscription'] as const;

export function useNotifications(page = 1, limit = 10) {
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, page, limit],
    queryFn: (): Promise<PaginatedResponse<Notification>> =>
      apiClient.get(`/notifications?page=${page}&limit=${limit}`),
  });
}

export function useUnreadCountQuery() {
  const setUnreadCount = useNotificationStore(s => s.setUnreadCount);
  const query = useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: (): Promise<{ count: number }> => apiClient.get('/notifications/unread-count'),
  });
  useEffect(() => {
    if (query.data) setUnreadCount(query.data.count);
  }, [query.data, setUnreadCount]);
  return query;
}

export function useMarkAllRead() {
  const queryClient = useQueryClient();
  const setUnreadCount = useNotificationStore(s => s.setUnreadCount);
  return useMutation({
    mutationFn: (): Promise<{ updated: number }> => apiClient.post('/notifications/read-all', {}),
    onSuccess: () => {
      setUnreadCount(0);
      void queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}

export function useMarkOneRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string): Promise<{ ok: boolean }> =>
      apiClient.patch(`/notifications/${id}/read`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      void queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    },
  });
}

export function useTableSubscription(tableId: string) {
  return useQuery({
    queryKey: subscriptionKey(tableId),
    queryFn: (): Promise<NotificationSubscription> =>
      apiClient.get(`/tables/${tableId}/subscription`),
    enabled: !!tableId,
  });
}

export function useSubscribeTable(tableId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (): Promise<NotificationSubscription> =>
      apiClient.post(`/tables/${tableId}/subscribe`, {}),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: subscriptionKey(tableId) });
      const prev = queryClient.getQueryData<NotificationSubscription>(subscriptionKey(tableId));
      queryClient.setQueryData(subscriptionKey(tableId), { subscribed: true });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      queryClient.setQueryData(subscriptionKey(tableId), ctx?.prev);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: subscriptionKey(tableId) });
    },
  });
}

export function useUnsubscribeTable(tableId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (): Promise<NotificationSubscription> =>
      apiClient.del(`/tables/${tableId}/subscribe`),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: subscriptionKey(tableId) });
      const prev = queryClient.getQueryData<NotificationSubscription>(subscriptionKey(tableId));
      queryClient.setQueryData(subscriptionKey(tableId), { subscribed: false });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      queryClient.setQueryData(subscriptionKey(tableId), ctx?.prev);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: subscriptionKey(tableId) });
    },
  });
}
